import { css } from "@emotion/css";
import { fontWeight, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import ConfigureTargetHelpImage from "~/areas/infrastructure/components/MachineSettings/Endpoints/Images/k8s-agent-configure-target-help.svg";
import ConfigureWorkerHelpImage from "~/areas/infrastructure/components/MachineSettings/Endpoints/Images/k8s-agent-configure-worker-help.svg";
import InstallHelpImage from "~/areas/infrastructure/components/MachineSettings/Endpoints/Images/k8s-agent-install-help.svg";
import NfsTargetHelpImage from "~/areas/infrastructure/components/MachineSettings/Endpoints/Images/k8s-agent-nfs-target-help.svg";
import NfsWorkerHelpImage from "~/areas/infrastructure/components/MachineSettings/Endpoints/Images/k8s-agent-nfs-worker-help.svg";
import OPCDefaultPermissionsHelpImage from "~/areas/infrastructure/components/MachineSettings/Endpoints/Images/opc-configure-default-help.svg";
import OPCInstallHelpImage from "~/areas/infrastructure/components/MachineSettings/Endpoints/Images/opc-install-help.svg";
import { EndpointSelectionScope } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
const helpStyles = {
    content: css({ marginTop: 0, h2: { marginTop: 0, fontWeight: fontWeight["700"], color: themeTokens.color.dialog.helpPanel.text }, p: { color: themeTokens.color.dialog.helpPanel.text } }),
};
type TypedHelpContentProps = {
    machineType: EndpointSelectionScope;
};
export function TargetConfigurationHelpContent() {
    return (<span className={helpStyles.content}>
            <h2>Connect seamlessly to Kubernetes </h2>
            <p>Installing the Kubernetes agent in your cluster eliminates the need for firewall adjustments or extra authentication.</p>
            <p>The agent automatically updates itself during regular health checks to keep things running smoothly and securely.</p>
        </span>);
}
export function targetConfigurationHelpImage() {
    return { src: ConfigureTargetHelpImage, altText: "" };
}
export function WorkerConfigurationHelpContent() {
    return (<span className={helpStyles.content}>
            <h2>Autoscale workers with Kubernetes</h2>
            <p>The Octopus Kubernetes worker can efficiently execute deployments and standalone tasks for a variety of deployment targets.</p>
            <p>It consumes minimal resources when idle, but will instantly scale to accommodate new tasks as they come in.</p>
        </span>);
}
export function workerConfigurationHelpImage() {
    return { src: ConfigureWorkerHelpImage, altText: "" };
}
export function TentacleInstallationHelpContent(props: TypedHelpContentProps) {
    const machineTypeString = props.machineType === EndpointSelectionScope.Worker ? "worker" : "deployment target";
    return (<span className={helpStyles.content}>
            <h2>What happens after installation?</h2>
            <p>After running the Helm command, your new Kubernetes {machineTypeString} will automatically be registered in Octopus Deploy.</p>
            {props.machineType === EndpointSelectionScope.DeploymentTarget && (<>
                    <p>You can then deploy to it using our pre-built steps for YAML, Helm or Kustomize.</p>
                    <p>The agent automatically updates itself during regular health checks to keep things running smoothly and securely.</p>
                </>)}
            <p>
                Need help installing Helm? <ExternalLink href={"https://helm.sh/docs/intro/install/"}>Refer to the Helm docs</ExternalLink>
            </p>
        </span>);
}
export function tentacleInstallationHelpImage() {
    return { src: InstallHelpImage, altText: "" };
}
export function NfsDriverInstallationHelpContent(props: {
    machineType: EndpointSelectionScope;
}) {
    return (<span className={helpStyles.content}>
            <h2>Simplify installation with NFS</h2>
            <p>Network File System (NFS) provides a simple way to host shared storage that doesn’t require you to create volumes in your cluster.</p>
            <p>Run the command to install the NFS CSI driver, then click next for the Helm command to install the Octopus Kubernetes {props.machineType === EndpointSelectionScope.Worker ? "worker" : "agent"}. </p>
        </span>);
}
export function targetNfsDriverInstallationHelpImage() {
    return { src: NfsTargetHelpImage, altText: "" };
}
export function workerNfsDriverInstallationHelpImage() {
    return { src: NfsWorkerHelpImage, altText: "" };
}
export function OctopusPermissionsControllerHelpContent() {
    return (<span className={helpStyles.content}>
            <h2>Octopus Permissions Controller</h2>
            <p>The Permissions Controller applies Kubernetes RBAC rules to deployment tasks executed by the Kubernetes agent, giving you granular control over permissions.</p>
            <p>Deployment tasks will run under a Service Account with a known set of roles.</p>
            <p>Each project, tenant, and environment can have exactly the permissions they require within a cluster.</p>
        </span>);
}
export function octopusPermissionsControllerHelpImage() {
    return { src: OPCInstallHelpImage, altText: "" };
}
export function DefaultPermissionsHelpContent() {
    return (<span className={helpStyles.content}>
            <h2>How granular permissions work</h2>
            <p>
                When running a deployment in your cluster, we'll annotate workload pods with the associated Octopus Project, Environment, Tenant and Step. This is the <strong>scope</strong> of the workload pod.
            </p>
            <p>
                To give your deployments privileges inside your Kubernetes cluster, you can use <strong>Workload Service Accounts</strong> to assign permissions to a given scope.
            </p>
            <p>
                The <strong>Octopus Permissions Controller</strong> then ensures that each workload receives only the assigned permissions.
            </p>
        </span>);
}
export function defaultPermissionsHelpImage() {
    return { src: OPCDefaultPermissionsHelpImage, altText: "" };
}
